import { ReactNode, useEffect, useState } from 'react'
import { useIntersectionObserver } from '@/unity-apps/hooks/useIntersectionObserver'

interface LazyLoadWrapperProps {
  children: ReactNode
  placeholder?: ReactNode
  rootMargin?: string
  triggerOffset?: string // For loading a bit earlier before the component is visible
  /**
   * If true, component will toggle between children and placeholder based on visibility.
   * If false (default), it will render children once visible even if scrolled out of view.
   */
  toggleOnVisibility?: boolean
}

/**
 * Wrapper component that renders its children when they are visible in the viewport
 * or about to be visible (based on triggerOffset).
 * Can be configured to toggle between children and placeholder based on visibility.
 */
export function LazyLoadWrapper({
  children,
  placeholder,
  rootMargin = '0px',
  triggerOffset = '200px', // Load when component is 200px away from viewport by default
  toggleOnVisibility = false,
}: LazyLoadWrapperProps) {
  const [ref, isVisible] = useIntersectionObserver<HTMLDivElement>({
    rootMargin: triggerOffset || rootMargin,
    threshold: 0,
  })

  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (toggleOnVisibility) {
      // In toggle mode, simply follow visibility
      setShouldRender(isVisible)
    } else {
      // In default mode, once visible always render (original behavior)
      if (isVisible && !shouldRender) {
        setShouldRender(true)
      }
    }
  }, [isVisible, shouldRender, toggleOnVisibility])

  return (
    <div ref={ref} className="h-full w-full">
      {shouldRender ? children : placeholder}
    </div>
  )
}
